import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Salad, Users, Radar, MapPin, Send, ListChecks, Plus, Search,
  RefreshCw, Copy, RotateCcw, ExternalLink, Trash2, Mail, Clock,
  AlertCircle, Check, CheckCircle2, XCircle, Loader2, Sparkles, Rocket,
  Globe, Flame, X, ArrowLeftRight, Pencil, Play, Coffee, Dumbbell, Mountain,
  Paperclip, Upload, FileText, FileImage, FileVideo,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { getPipelineConfig } from "./pipelineConfigs";
import { StatusBadge } from "./B2CIntelHome";

// Every other pipeline can move leads TO these targets — including the
// staging pool "b2c_intel" so misidentified consumer leads can be sent
// back for re-triage. We compute the list dynamically per pipeline so a
// pipeline never lists itself as a target.
const MOVE_TARGETS_ALL = [
  { key: "b2c_intel",    label: "B2C Intelligence (staging)", icon: Users,    accent: "text-primary" },
  { key: "b2c_coffee",   label: "B2C Coffee",                 icon: Coffee,   accent: "text-amber-600" },
  { key: "b2c_jaydiets", label: "B2C JAYDIETs",               icon: Salad,    accent: "text-emerald-600" },
  { key: "b2c_wellness", label: "B2C Wellness",               icon: Dumbbell, accent: "text-fuchsia-600" },
  { key: "b2c_retreat",  label: "B2C Retreat",                icon: Mountain, accent: "text-sky-600" },
];

const TABS = [
  { key: "leads", label: "Leads", icon: Users },
  { key: "discover", label: "Discover", icon: Radar },
  { key: "cities", label: "Target Cities", icon: MapPin },
  { key: "outreach", label: "Outreach", icon: Send },
  { key: "queue", label: "Send Queue", icon: ListChecks },
];

// Pipelines whose Discover tab is backed by real catalog data. Others
// hide the tab entirely (or show a "not available yet" note) rather than
// pretending to run and always returning 0 rows.
const DISCOVER_PIPELINES = new Set(["b2c_jaydiets"]);

// `pipeline` prop switches the entire page's content (JAYDIETs / Coffee /
// Wellness / Retreat). Defaults to b2c_jaydiets so the existing route still
// works without a prop.
export default function PipelineHome({ pipeline = "b2c_jaydiets" }) {
  const config = getPipelineConfig(pipeline);
  const BRAND = config.brand;
  const PIPELINE = config.key;
  const HeaderIcon = config.icon || Salad;

  const [tab, setTab] = useState("leads");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [selection, setSelection] = useState({});  // { lead_id: true }
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((n) => n + 1);

  // Whenever the pipeline changes (user navigates between /app/b2c/coffee
  // and /app/b2c/wellness) reset internal state so we don't leak selections
  // or refresh-keys across pipelines.
  useEffect(() => {
    setTab("leads");
    setSelection({});
    setBulkOpen(false);
    setWebhookOpen(false);
    setRefreshKey((n) => n + 1);
  }, [PIPELINE]);

  // Coerce the current tab to a valid one for this pipeline. Prevents rendering
  // the Discover panel on Coffee/Wellness/Retreat where the tab is hidden.
  const validTabs = TABS.filter((t) => t.key !== "discover" || DISCOVER_PIPELINES.has(PIPELINE)).map((t) => t.key);
  const activeTab = validTabs.includes(tab) ? tab : "leads";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid={`pipeline-home-${PIPELINE}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={`text-[10px] uppercase tracking-[0.28em] ${config.accentClass} inline-flex items-center gap-2`}>
            <HeaderIcon className="h-3 w-3" /> {config.kicker}
          </div>
          <h1 className="font-display text-4xl mt-1">{config.tagline}</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            {config.description}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full gap-1.5" onClick={() => setWebhookOpen(true)} data-testid="jd-webhook-btn">
            <Globe className="h-3.5 w-3.5" /> Webhook URL
          </Button>
          <Button className="rounded-full gap-1.5" onClick={() => setBulkOpen(true)}
                  data-testid="jd-bulk-outreach-btn"
                  disabled={Object.keys(selection).length === 0}>
            <Send className="h-3.5 w-3.5" /> Bulk Outreach
            {Object.keys(selection).length > 0 && (
              <span className="ml-1 h-5 min-w-[20px] rounded-full bg-primary-foreground text-primary px-1.5 text-xs font-medium">
                {Object.keys(selection).length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.filter((t) => t.key !== "discover" || DISCOVER_PIPELINES.has(PIPELINE)).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`jd-tab-${key}`}
              className={
                "px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 transition -mb-px " +
                (activeTab === key
                  ? "border-primary text-foreground font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Panel content */}
      {activeTab === "leads" && (
        <LeadsPanel PIPELINE={PIPELINE} BRAND={BRAND} config={config} selection={selection} setSelection={setSelection} refreshKey={refreshKey} onBump={bumpRefresh} />
      )}
      {activeTab === "discover" && <DiscoverPanel PIPELINE={PIPELINE} BRAND={BRAND} config={config} onBump={bumpRefresh} />}
      {activeTab === "cities" && <CitiesPanel PIPELINE={PIPELINE} BRAND={BRAND} config={config} onBump={bumpRefresh} />}
      {activeTab === "outreach" && <OutreachPanel PIPELINE={PIPELINE} BRAND={BRAND} />}
      {activeTab === "queue" && <QueuePanel PIPELINE={PIPELINE} BRAND={BRAND} refreshKey={refreshKey} />}

      {bulkOpen && (
        <BulkOutreachDialog
          PIPELINE={PIPELINE}
          BRAND={BRAND}
          selection={selection}
          onClose={() => setBulkOpen(false)}
          onSent={() => {
            setSelection({});
            bumpRefresh();
            setTab("queue");
            setBulkOpen(false);  // ← must close the dialog or its overlay traps clicks
          }}
        />
      )}
      {webhookOpen && <WebhookDialog onClose={() => setWebhookOpen(false)} />}
    </div>
  );
}


/* -------------------- Leads panel -------------------- */

function LeadsPanel({ PIPELINE, BRAND, config, selection, setSelection, refreshKey, onBump }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pipeline: PIPELINE, limit: "200" });
      if (q.trim()) params.set("search", q.trim());
      const { data } = await api.get(`/growth/leads?${params.toString()}`);
      setLeads(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const selectedIds = Object.keys(selection);

  const moveToPipeline = async (targetKey) => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post("/growth/leads/bulk-move", {
        lead_ids: selectedIds, to_pipeline: targetKey,
      });
      toast.success(`Moved ${data.moved} lead${data.moved === 1 ? "" : "s"} → ${targetKey.replace("b2c_", "")}`);
      setSelection({});
      onBump && onBump();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const toggleAll = () => {
    if (Object.keys(selection).length === leads.length) setSelection({});
    else setSelection(Object.fromEntries(leads.filter((l) => l.email).map((l) => [l.id, true])));
  };

  const moveTargets = MOVE_TARGETS_ALL.filter((t) => t.key !== PIPELINE);

  return (
    <div className="space-y-4" data-testid="jd-leads-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search company, email, city…"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-border bg-background text-sm"
            data-testid="jd-leads-search"
          />
        </div>
        <Button variant="ghost" size="sm" className="rounded-full" onClick={load} data-testid="jd-leads-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {leads.length} lead{leads.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Bulk-move bar — appears only when leads are checked. Lets the operator
          send mis-identified leads BACK to B2C Intelligence or across to any
          other pipeline. */}
      {selectedIds.length > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 flex items-center gap-3 flex-wrap" data-testid="jd-selection-bar">
          <div className="text-sm font-medium">{selectedIds.length} selected</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full gap-1.5 ml-auto" data-testid="jd-move-btn">
                <ArrowLeftRight className="h-3.5 w-3.5" /> Move to pipeline
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Move {selectedIds.length} lead{selectedIds.length === 1 ? "" : "s"} to…</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {moveTargets.map((t) => (
                <DropdownMenuItem key={t.key} onClick={() => moveToPipeline(t.key)} data-testid={`jd-move-to-${t.key}`}>
                  <t.icon className={"h-4 w-4 mr-2 " + t.accent} /> {t.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSelection({})} data-testid="jd-clear-selection">Clear</Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <config.icon className="h-6 w-6 mx-auto mb-2 text-emerald-500 opacity-60" />
          <div className="font-medium">No {config.label} leads yet</div>
          <div className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            Head to <b>B2C Intel</b> to upload leads, then move them into this pipeline.
            Or use <b>Discover</b> to pull from the catalog, or paste your Webhook URL into Make/Apify.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={leads.length > 0 && Object.keys(selection).length === leads.filter((l) => l.email).length}
                    onChange={toggleAll}
                    data-testid="jd-leads-select-all"
                  />
                </th>
                <th className="text-left px-3 py-3">Business</th>
                <th className="text-left px-3 py-3">City</th>
                <th className="text-left px-3 py-3">Industry</th>
                <th className="text-left px-3 py-3">Source</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`jd-lead-row-${l.id}`}>
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={!!selection[l.id]}
                      onChange={(e) => setSelection((s) => {
                        const next = { ...s };
                        if (e.target.checked) next[l.id] = true; else delete next[l.id];
                        return next;
                      })}
                      disabled={!l.email}
                      title={!l.email ? "No email on file — can't include in bulk outreach" : ""}
                      data-testid={`jd-lead-check-${l.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/app/growth/leads/${l.id}`} className="font-medium hover:underline">
                      {l.business_name}
                    </Link>
                    {l.website && (
                      <div className="text-[11px] text-muted-foreground truncate max-w-[240px]">
                        <a href={l.website} target="_blank" rel="noreferrer" className="hover:underline">{l.website}</a>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{l.city || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground text-xs">{l.industry || "—"}</td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-secondary/60">{l.source || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={l.status || "new"} />
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[220px]">
                    {l.email ? l.email : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-amber-600">
                        <AlertCircle className="h-3 w-3" /> no email · not selectable
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* -------------------- Discover panel -------------------- */

function DiscoverPanel({ PIPELINE, BRAND, config, onBump }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [cityConfig, setCityConfig] = useState({ target_cities: [] });

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/outreach/brand-config/${BRAND}`); setCityConfig(data); } catch {}
  })(); }, [BRAND]);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/outreach/discover", { brand: BRAND, limit: 25 });
      setResult(data);
      onBump && onBump();
      toast.success(`Added ${data.added} · updated ${data.updated}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="jd-discover-panel">
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 grid place-items-center rounded-lg bg-emerald-500/15 text-emerald-600">
            <Radar className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-medium">Discover from the Kairosa catalog</div>
            <div className="text-sm text-muted-foreground mt-1">
              Pulls {config.label} prospects from Kairosa's curated global catalog and drops them into
              this pipeline. Reruns update existing rows — no duplicates.
            </div>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-2">
          <div className="text-xs text-muted-foreground">
            Searching cities:{" "}
            <span className="font-medium text-foreground">
              {cityConfig.target_cities?.length ? cityConfig.target_cities.join(", ") : "All catalog cities"}
            </span>
          </div>
          <div className="ml-auto">
            <Button onClick={run} disabled={busy} className="rounded-full gap-1.5" data-testid="jd-discover-run">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {busy ? "Discovering…" : "Run discovery"}
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="jd-discover-result">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-sm font-medium">Discovery complete</span>
          </div>
          <div className="mt-2 text-sm text-muted-foreground grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><div className="text-xs">Added</div><div className="text-lg font-semibold text-foreground tabular-nums">{result.added}</div></div>
            <div><div className="text-xs">Updated</div><div className="text-lg font-semibold text-foreground tabular-nums">{result.updated}</div></div>
            <div><div className="text-xs">Considered</div><div className="text-lg font-semibold text-foreground tabular-nums">{result.considered}</div></div>
            <div><div className="text-xs">Catalog size</div><div className="text-lg font-semibold text-foreground tabular-nums">{result.catalog_size}</div></div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Individual clients (B2C) can't be discovered from public search — set up a {config.label} landing form or
            wire Make/Apify to the <b>Webhook URL</b> to ingest them. Every lead ingested that way gets tagged
            <code className="text-xs px-1 rounded bg-secondary mx-1">brand={BRAND}</code> and appears on the Leads tab.
          </div>
        </div>
      </div>
    </div>
  );
}


/* -------------------- Cities panel -------------------- */

/**
 * Editable Target Cities.
 * Each city is a full row with: enabled toggle · country / city name (inline
 * editable) · notes (fed to Claude for search refinement) · Run Discovery.
 *
 * "Add city" and "Add country" buttons let the operator extend the seed list.
 * Everything is scoped by (tenant, pipeline) via /api/outreach/target-cities.
 */
function CitiesPanel({ PIPELINE, BRAND, config, onBump }) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // full city doc
  const [running, setRunning] = useState({});     // { id: boolean }
  const [expanded, setExpanded] = useState({});   // { country: boolean }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/outreach/target-cities?pipeline=${PIPELINE}`);
      setCities(data.items || []);
    } catch (e) {
      toast.error("Failed to load target cities");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [PIPELINE]);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of cities) {
      (g[c.country] = g[c.country] || []).push(c);
    }
    return g;
  }, [cities]);

  const toggleEnabled = async (city) => {
    try {
      const { data } = await api.patch(`/outreach/target-cities/${city.id}`, { enabled: !city.enabled });
      setCities((cs) => cs.map((c) => (c.id === city.id ? data : c)));
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const remove = async (city) => {
    if (!window.confirm(`Delete ${city.city}, ${city.country}?`)) return;
    try {
      await api.delete(`/outreach/target-cities/${city.id}`);
      setCities((cs) => cs.filter((c) => c.id !== city.id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const runDiscovery = async (city) => {
    setRunning((r) => ({ ...r, [city.id]: true }));
    try {
      const { data } = await api.post(`/outreach/target-cities/${city.id}/discover`, {});
      toast.success(`${city.city}: +${data.added} new · ${data.updated} updated`);
      // Reload so we get the updated timestamps + counters
      await load();
      onBump && onBump();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally {
      setRunning((r) => ({ ...r, [city.id]: false }));
    }
  };

  const enabledCount = cities.filter((c) => c.enabled).length;

  return (
    <div className="space-y-5" data-testid="jd-cities-panel">
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Target cities</div>
            <div className="text-sm">
              Cities {config?.label || "this pipeline"} focuses on. Each row uses its own 9:30 AM local time for outreach.
              Notes below are fed to Kairosa AI to steer discovery.
            </div>
          </div>
          <Button size="sm" className="rounded-full gap-1.5" onClick={() => setAddOpen(true)} data-testid="cities-add-btn">
            <Plus className="h-3.5 w-3.5" /> Add city
          </Button>
        </div>

        <div className="mt-3 text-xs text-muted-foreground">
          {enabledCount > 0
            ? <><span className="text-emerald-600 font-medium">{enabledCount}</span> enabled for outreach · {cities.length} total</>
            : <>None enabled yet — toggle at least one row on.</>
          }
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground mt-4">Loading…</div>
        ) : cities.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center mt-4">
            <MapPin className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
            <div className="font-medium">No target cities yet</div>
            <div className="text-xs text-muted-foreground mt-1">Click "Add city" to define your first target.</div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {Object.keys(grouped).sort().map((country) => {
              const list = grouped[country];
              const on = list.filter((c) => c.enabled).length;
              const isOpen = expanded[country] ?? (on > 0 || list.length <= 6);
              return (
                <div key={country} className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [country]: !isOpen }))}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/40 text-left"
                    data-testid={`cities-country-${country.replace(/\s+/g, '-')}`}
                  >
                    <div className="text-sm font-medium flex items-center gap-2">
                      {country}
                      {on > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">{on} enabled</span>}
                      <span className="text-[10px] text-muted-foreground">· {list.length}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">{isOpen ? "▾" : "▸"}</span>
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-border">
                      {list.map((c) => (
                        <div key={c.id} className="p-3 flex items-start gap-3 hover:bg-secondary/30" data-testid={`cities-row-${c.id}`}>
                          <input
                            type="checkbox"
                            checked={!!c.enabled}
                            onChange={() => toggleEnabled(c)}
                            className="mt-1"
                            data-testid={`cities-toggle-${c.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-medium text-sm">{c.city}</div>
                              {c.last_discovery_at && (
                                <span className="text-[10px] text-muted-foreground" title={c.last_plan_summary || ""}>
                                  last: +{c.last_discovery_added ?? 0} · {new Date(c.last_discovery_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {c.notes && (
                              <div className="mt-1 text-[11px] text-muted-foreground line-clamp-2 max-w-xl">
                                <span className="text-primary/70">notes:</span> {c.notes}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="ghost" className="h-8 rounded gap-1" onClick={() => setEditing(c)} data-testid={`cities-edit-${c.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 rounded gap-1"
                              onClick={() => runDiscovery(c)}
                              disabled={!!running[c.id] || !c.enabled}
                              title={!c.enabled ? "Enable the city first" : "Run discovery for this city"}
                              data-testid={`cities-run-${c.id}`}
                            >
                              {running[c.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                              <span className="text-xs">Run</span>
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 rounded gap-1 text-destructive hover:text-destructive" onClick={() => remove(c)} data-testid={`cities-delete-${c.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {addOpen && (
        <CityDialog
          pipeline={PIPELINE}
          onClose={() => setAddOpen(false)}
          onSaved={(created) => { setCities((cs) => [...cs, created]); setAddOpen(false); }}
        />
      )}
      {editing && (
        <CityDialog
          pipeline={PIPELINE}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => { setCities((cs) => cs.map((c) => c.id === updated.id ? updated : c)); setEditing(null); }}
        />
      )}
    </div>
  );
}


function CityDialog({ pipeline, existing = null, onClose, onSaved }) {
  const [country, setCountry] = useState(existing?.country || "");
  const [city, setCity] = useState(existing?.city || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!country.trim() || !city.trim()) {
      toast.error("Country and city are required");
      return;
    }
    setSaving(true);
    try {
      if (existing) {
        const { data } = await api.patch(`/outreach/target-cities/${existing.id}`, {
          country: country.trim(), city: city.trim(), notes: notes.trim(), enabled,
        });
        onSaved(data);
      } else {
        const { data } = await api.post("/outreach/target-cities", {
          pipeline, country: country.trim(), city: city.trim(),
          notes: notes.trim() || null, enabled,
        });
        onSaved(data);
      }
      toast.success(existing ? "Updated" : "Added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="city-dialog">
        <DialogHeader>
          <DialogTitle className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-normal">
            {existing ? "Edit target city" : "Add a target city"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs">Country</label>
            <input value={country} onChange={(e) => setCountry(e.target.value)}
                   placeholder="e.g. Portugal"
                   className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                   data-testid="city-country-input" />
          </div>
          <div>
            <label className="text-xs">City</label>
            <input value={city} onChange={(e) => setCity(e.target.value)}
                   placeholder="e.g. Lisbon"
                   className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                   data-testid="city-name-input" />
          </div>
          <div>
            <label className="text-xs flex items-center gap-1.5">
              Notes for Kairosa AI
              <span className="text-[10px] text-muted-foreground normal-case">(steer discovery)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="e.g. Focus on premium boutique studios, avoid chains and franchises."
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="city-notes-input"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Kairosa AI (Claude Sonnet 4.5) will read these notes and refine the search plan when you Run Discovery.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="city-enabled-toggle" />
            <span className="text-xs">Enabled for outreach & discovery</span>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="city-save-btn">
              {saving ? "Saving…" : (existing ? "Save" : "Add city")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* -------------------- Outreach panel (template management) -------------------- */


/* Whitelist mirrors the backend ALLOWED_EXTS in outreach_attachments.py.
   Kept in sync so the file-picker rejects unsupported types before upload. */
const ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.png,.jpg,.jpeg,.svg,.webp,.gif,.mp4,.mov,.webm," +
  ".ppt,.pptx,.ppsx,.xls,.xlsx,.csv,.txt,.zip";
const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
const ATTACHMENT_MAX_COUNT = 5;


function humanBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}


function fileIconFor(mime) {
  if (!mime) return FileText;
  if (mime.startsWith("image/")) return FileImage;
  if (mime.startsWith("video/")) return FileVideo;
  return FileText;
}


/**
 * TemplateAttachments
 * ------------------
 * Compact file-picker used inside the New / Edit Template dialog. Uploads
 * files directly to POST /outreach/attachments/upload, then reports the
 * hydrated attachment metadata up to the parent via `onChange`.
 *
 * - Enforces max 5 files, max 25 MB each (same as backend).
 * - Chip list shows filename + size + file-type icon + remove button.
 * - `Remove` unhooks from the local list; the actual file stays in storage
 *   (harmless — soft-delete is done from a dedicated attachments picker if
 *   ever needed).
 */
function TemplateAttachments({ attachments, onChange }) {
  const inputRef = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (file) => {
    if (!file) return;
    if (attachments.length >= ATTACHMENT_MAX_COUNT) {
      toast.error(`Max ${ATTACHMENT_MAX_COUNT} attachments per template`);
      return;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      toast.error(`${file.name} is too large (max 25 MB)`);
      return;
    }
    uploadOne(file);
  };

  const uploadOne = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/outreach/attachments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...(attachments || []), data]);
      toast.success(`Attached: ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = (id) => {
    onChange((attachments || []).filter((a) => a.id !== id));
  };

  return (
    <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-3" data-testid="jd-tpl-attachments">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5 text-primary" />
          Attachments
          <span className="text-[10px] text-muted-foreground normal-case">
            · PDF, DOCX, images, MP4, PPTX &c · max {ATTACHMENT_MAX_COUNT} files · 25 MB each
          </span>
        </label>
        <label className={"inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs cursor-pointer hover:border-primary/50 transition " + (busy ? "opacity-60 pointer-events-none" : "")}>
          <Upload className="h-3.5 w-3.5" />
          {busy ? "Uploading…" : "Add file"}
          <input
            type="file"
            accept={ATTACHMENT_ACCEPT}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";  // reset so same-file re-select works
              pick(f);
            }}
            className="hidden"
            data-testid="jd-tpl-attach-input"
          />
        </label>
      </div>

      {(attachments || []).length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {attachments.map((a) => {
            const Icon = fileIconFor(a.content_type);
            return (
              <div
                key={a.id}
                data-testid={`jd-tpl-attach-chip-${a.id}`}
                className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5"
              >
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0 text-xs truncate">{a.filename}</div>
                <div className="text-[10px] text-muted-foreground tabular-nums">{humanBytes(a.size)}</div>
                <button
                  type="button"
                  onClick={() => remove(a.id)}
                  className="p-0.5 rounded hover:bg-destructive/15 text-destructive"
                  aria-label={`Remove ${a.filename}`}
                  data-testid={`jd-tpl-attach-remove-${a.id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function OutreachPanel({ PIPELINE, BRAND }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch by pipeline so the backend seeds all 13 templates on first hit.
      const { data } = await api.get(`/outreach/templates?pipeline=${PIPELINE}`);
      setTemplates(data.items || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [PIPELINE]);

  const saveTemplate = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const attachment_ids = (editing.attachments || []).map((a) => a.id);
      if (editing.id) {
        await api.patch(`/outreach/templates/${editing.id}`, {
          name: editing.name, subject: editing.subject, body: editing.body,
          from_name: editing.from_name || null, reply_to: editing.reply_to || null,
          sequence_step: editing.sequence_step ?? null,
          attachment_ids,
        });
      } else {
        await api.post("/outreach/templates", {
          brand: BRAND, pipeline: PIPELINE,
          name: editing.name, subject: editing.subject, body: editing.body,
          from_name: editing.from_name || null, reply_to: editing.reply_to || null,
          sequence_step: editing.sequence_step ?? null,
          attachment_ids,
        });
      }
      toast.success("Template saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Open editor with hydrated attachment objects (id + filename + size) so we
  // can render the chips immediately. Passed to the form via editing.attachments.
  const openEditor = async (tpl) => {
    if (!tpl) {
      setEditing({ name: "", subject: "", body: "", attachments: [] });
      return;
    }
    const ids = tpl.attachment_ids || [];
    if (!ids.length) {
      setEditing({ ...tpl, attachments: [] });
      return;
    }
    // Hydrate attachment metadata via the list endpoint (small — <=5).
    try {
      const { data } = await api.get("/outreach/attachments");
      const known = Object.fromEntries((data.items || []).map((a) => [a.id, a]));
      const attachments = ids.map((id) => known[id]).filter(Boolean);
      setEditing({ ...tpl, attachments });
    } catch {
      setEditing({ ...tpl, attachments: [] });
    }
  };

  const removeTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/outreach/templates/${id}`);
      toast.success("Template deleted");
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="jd-outreach-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          Every template supports dynamic tags: <code className="text-xs px-1 rounded bg-secondary">[Company Name]</code>,{" "}
          <code className="text-xs px-1 rounded bg-secondary">[First Name]</code>,{" "}
          <code className="text-xs px-1 rounded bg-secondary">[City]</code>,{" "}
          <code className="text-xs px-1 rounded bg-secondary">[Country]</code>,{" "}
          <code className="text-xs px-1 rounded bg-secondary">[Industry]</code>.
        </div>
        <Button className="rounded-full gap-1.5" onClick={() => openEditor(null)} data-testid="jd-tpl-new">
          <Plus className="h-3.5 w-3.5" /> New template
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-border bg-card p-4" data-testid={`jd-tpl-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate flex items-center gap-2">
                    {typeof t.sequence_step === "number" && (
                      <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">
                        {t.sequence_step === 0 ? "INTRO" : `F/UP ${t.sequence_step}`}
                      </span>
                    )}
                    <span className="truncate">{t.name}</span>
                    {t.is_seed && <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">Seed</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{t.subject}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => openEditor(t)} data-testid={`jd-tpl-edit-${t.id}`}>Edit</Button>
                  {!t.is_seed && (
                    <button onClick={() => removeTemplate(t.id)} className="h-8 w-8 grid place-items-center rounded hover:bg-destructive/20 text-destructive" data-testid={`jd-tpl-delete-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground line-clamp-4 whitespace-pre-wrap">{t.body}</div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Dialog open={true} onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-2xl" data-testid="jd-tpl-editor">
            <DialogHeader>
              <DialogTitle>{editing.id ? "Edit template" : "New template"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs">Name</label>
                <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="jd-tpl-name" />
              </div>
              <div>
                <label className="text-xs">Sequence step</label>
                <select
                  value={editing.sequence_step ?? ""}
                  onChange={(e) => setEditing({ ...editing, sequence_step: e.target.value === "" ? null : Number(e.target.value) })}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                  data-testid="jd-tpl-sequence-step"
                >
                  <option value="">Ad-hoc (not in sequence)</option>
                  <option value="0">Intro (Seed)</option>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <option key={i + 1} value={i + 1}>Follow-up {i + 1}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs">Subject</label>
                <input value={editing.subject || ""} onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                       className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="jd-tpl-subject" />
              </div>
              <div>
                <label className="text-xs">Body</label>
                <textarea value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                          rows={10}
                          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono" data-testid="jd-tpl-body" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs">From name (optional)</label>
                  <input value={editing.from_name || ""} onChange={(e) => setEditing({ ...editing, from_name: e.target.value })}
                         className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" />
                </div>
                <div>
                  <label className="text-xs">Reply-to (optional)</label>
                  <input value={editing.reply_to || ""} onChange={(e) => setEditing({ ...editing, reply_to: e.target.value })}
                         className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" />
                </div>
              </div>

              {/* Attachments — up to 5 files (25 MB each). Files ride along on
                  every send using this template. */}
              <TemplateAttachments
                attachments={editing.attachments || []}
                onChange={(next) => setEditing((e) => ({ ...e, attachments: next }))}
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveTemplate} disabled={saving} data-testid="jd-tpl-save">
                  {saving ? "Saving…" : "Save template"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


/* -------------------- Queue panel -------------------- */

function QueuePanel({ PIPELINE, BRAND, refreshKey }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tick, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pipeline: PIPELINE, limit: "200" });
      if (filter !== "all") params.set("status", filter);
      const { data } = await api.get(`/outreach/queue?${params.toString()}`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Failed to load queue");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, refreshKey, tick, PIPELINE]);

  const sendNow = async (row) => {
    try {
      await api.post(`/outreach/queue/${row.id}/send-now`);
      toast.success("Dispatched");
      setTick((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const cancel = async (row) => {
    if (!window.confirm("Cancel this queued email?")) return;
    try {
      await api.delete(`/outreach/queue/${row.id}`);
      toast.success("Cancelled");
      setTick((n) => n + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div className="space-y-4" data-testid="jd-queue-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: "all", label: "All" },
          { key: "queued", label: `Queued (${counts.queued || 0})` },
          { key: "sent", label: `Sent (${counts.sent || 0})` },
          { key: "failed", label: `Failed (${counts.failed || 0})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`jd-queue-filter-${f.key}`}
            className={
              "px-3 py-1 rounded-full text-xs border transition " +
              (filter === f.key ? "bg-primary text-primary-foreground border-primary"
                                 : "border-border hover:border-primary/50")
            }
          >
            {f.label}
          </button>
        ))}
        <Button variant="ghost" size="sm" className="rounded-full ml-auto" onClick={() => setTick((n) => n + 1)} data-testid="jd-queue-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <ListChecks className="h-6 w-6 mx-auto mb-2 opacity-60" />
          No {filter === "all" ? "" : filter} emails in the queue yet.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="text-left px-3 py-3">Recipient</th>
                <th className="text-left px-3 py-3">Subject</th>
                <th className="text-left px-3 py-3">Send at (local)</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="px-3 py-3 w-32" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <QueueRow key={r.id} row={r} onSendNow={sendNow} onCancel={cancel} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QueueRow({ row, onSendNow, onCancel }) {
  const localTime = useMemo(() => {
    if (!row.send_at) return "—";
    try {
      const d = new Date(row.send_at);
      const opts = { hour: "2-digit", minute: "2-digit", timeZone: row.recipient_tz || "UTC", timeZoneName: "short" };
      return d.toLocaleString(undefined, { ...opts, day: "2-digit", month: "short" });
    } catch { return row.send_at; }
  }, [row.send_at, row.recipient_tz]);

  const statusStyle = {
    queued: "bg-primary/15 text-primary",
    sent: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
    failed: "bg-destructive/15 text-destructive",
  }[row.status] || "bg-secondary";

  return (
    <tr className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`jd-queue-row-${row.id}`}>
      <td className="px-3 py-2.5">
        <div className="font-medium truncate max-w-[220px]">{row.recipient_name || row.recipient_email}</div>
        <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">
          {row.recipient_email} · {row.recipient_city || "—"}
        </div>
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[280px]">{row.subject}</td>
      <td className="px-3 py-2.5 text-xs">
        <div>{localTime}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{row.recipient_tz}</div>
      </td>
      <td className="px-3 py-2.5">
        <span className={"text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full " + statusStyle}>{row.status}</span>
        {row.error && <div className="text-[10px] text-destructive mt-1 max-w-[180px]">{row.error}</div>}
      </td>
      <td className="px-3 py-2.5">
        {row.status === "queued" && (
          <div className="flex items-center gap-1 justify-end">
            <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => onSendNow(row)} data-testid={`jd-queue-send-${row.id}`}>Send now</Button>
            <button onClick={() => onCancel(row)} className="h-7 w-7 grid place-items-center rounded hover:bg-destructive/20 text-destructive" data-testid={`jd-queue-cancel-${row.id}`}>
              <XCircle className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}


/* -------------------- Bulk Outreach dialog -------------------- */

function BulkOutreachDialog({ PIPELINE, BRAND, selection, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [smart, setSmart] = useState(true);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(30);
  const [previewLeadId, setPreviewLeadId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [queueing, setQueueing] = useState(false);
  const leadIds = Object.keys(selection);

  // Load templates + selected leads (for the preview dropdown)
  useEffect(() => { (async () => {
    try {
      const [t, l] = await Promise.all([
        api.get(`/outreach/templates?pipeline=${PIPELINE}`),
        api.get(`/growth/leads?pipeline=${PIPELINE}&limit=200`),
      ]);
      setTemplates(t.data.items || []);
      const selectedLeads = (l.data.items || []).filter((x) => selection[x.id]);
      setLeads(selectedLeads);
      if (t.data.items?.length) {
        // Prefer the intro (sequence_step=0), else the first template.
        const intro = t.data.items.find((x) => x.sequence_step === 0) || t.data.items[0];
        setTemplateId(intro.id);
        setSubject(intro.subject);
        setBody(intro.body);
      }
      if (selectedLeads.length) setPreviewLeadId(selectedLeads[0].id);
    } catch (e) { toast.error("Failed to load templates/leads"); }
  })(); /* eslint-disable-next-line */ }, []);

  const doPreview = async () => {
    if (!previewLeadId) return;
    try {
      const { data } = await api.post("/outreach/preview", { template_id: templateId, subject, body, lead_id: previewLeadId });
      setPreview(data);
    } catch (e) { toast.error("Preview failed"); }
  };
  useEffect(() => { if (previewLeadId) doPreview(); /* eslint-disable-next-line */ }, [previewLeadId]);

  const queueAll = async () => {
    setQueueing(true);
    try {
      const { data } = await api.post("/outreach/queue", {
        template_id: templateId,
        lead_ids: leadIds,
        subject_override: subject,
        body_override: body,
        smart_schedule: smart,
        send_hour: hour, send_minute: minute,
      });
      if (data.queued === 0) {
        toast.error("Nothing was queued. Check that the leads have valid emails.");
      } else {
        toast.success(`Queued ${data.queued} emails. ${data.skipped?.length ? `${data.skipped.length} skipped (no email).` : ""}`);
        onSent && onSent();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Queue failed"); }
    finally { setQueueing(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="jd-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-normal">
            Bulk outreach · {leadIds.length} lead{leadIds.length === 1 ? "" : "s"} selected
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Left: editor */}
          <div className="space-y-3">
            <div>
              <label className="text-xs">Template</label>
              <select
                value={templateId || ""}
                onChange={(e) => {
                  const t = templates.find((x) => x.id === e.target.value);
                  if (t) { setTemplateId(t.id); setSubject(t.subject); setBody(t.body); }
                }}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                data-testid="jd-bulk-template"
              >
                {templates.map((t) => {
                  const stepLabel = t.sequence_step === 0 ? "Intro" :
                                    typeof t.sequence_step === "number" ? `Follow-up ${t.sequence_step}` :
                                    "Ad-hoc";
                  return <option key={t.id} value={t.id}>{stepLabel} — {t.name}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="text-xs">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                     className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="jd-bulk-subject" />
            </div>
            <div>
              <label className="text-xs">Body</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono" data-testid="jd-bulk-body" />
              <div className="mt-1 text-[10px] text-muted-foreground">
                Tags: [Company Name], [First Name], [City], [Country], [Industry], [Email], [Phone]
              </div>
            </div>

            <div className="rounded-md border border-border bg-secondary/30 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs">
                  <div className="font-medium">Smart Scheduler</div>
                  <div className="text-muted-foreground">Send at recipient's local time</div>
                </div>
                <Switch checked={smart} onCheckedChange={setSmart} data-testid="jd-bulk-smart" />
              </div>
              {smart && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Hour (24h)</label>
                    <input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(Number(e.target.value))}
                           className="mt-1 w-full h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="jd-bulk-hour" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Minute</label>
                    <input type="number" min={0} max={59} value={minute} onChange={(e) => setMinute(Number(e.target.value))}
                           className="mt-1 w-full h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="jd-bulk-minute" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: preview */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-xs">Preview for</label>
              <select value={previewLeadId || ""} onChange={(e) => setPreviewLeadId(e.target.value)}
                      className="flex-1 h-8 rounded-md border border-border bg-background px-2 text-sm" data-testid="jd-bulk-preview-lead">
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>{l.business_name} — {l.city || "?"}</option>
                ))}
              </select>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={doPreview}><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>

            <div className="rounded-md border border-border bg-background p-3 text-sm max-h-96 overflow-y-auto" data-testid="jd-bulk-preview">
              {preview ? (
                <>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">To</div>
                  <div className="text-xs mb-2">{preview.recipient_email || "(no email)"} <span className="text-muted-foreground">· {preview.recipient_tz}</span></div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Subject</div>
                  <div className="text-sm font-medium mb-2">{preview.subject}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Body</div>
                  <div className="text-xs whitespace-pre-wrap">{preview.body}</div>
                </>
              ) : (
                <div className="text-muted-foreground text-xs">Pick a lead to preview the rendered email.</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Emails without a valid recipient are silently skipped.
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={queueAll} disabled={queueing || !templateId} data-testid="jd-bulk-queue">
              {queueing ? "Queueing…" : `Queue ${leadIds.length} email${leadIds.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* -------------------- Webhook config dialog -------------------- */

function WebhookDialog({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/webhook/config");
      setCfg(data);
    } catch (e) { toast.error("Failed to load webhook config"); }
  };
  useEffect(() => { load(); }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the webhook secret? Any external tool using the old URL will stop working immediately.")) return;
    setRotating(true);
    try {
      await api.post("/webhook/config/rotate");
      await load();
      toast.success("Secret rotated. Update your Make/Apify/Zapier setups.");
    } catch (e) { toast.error("Rotate failed"); }
    finally { setRotating(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="jd-webhook-dialog">
        <DialogHeader>
          <DialogTitle className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-normal">
            Webhook · Ingest leads from Make, Apify, Zapier
          </DialogTitle>
        </DialogHeader>

        {!cfg ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-xs mb-1">Your ingest URL</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 rounded-md border border-border bg-secondary/40 px-3 py-2 text-[11px] font-mono overflow-x-auto" data-testid="jd-webhook-url">
                  {cfg.url}
                </code>
                <Button variant="outline" size="sm" onClick={() => copy(cfg.url)} className="rounded-md" data-testid="jd-webhook-copy">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">POST · Content-Type application/json · no auth headers required</div>
            </div>

            <div>
              <div className="text-xs mb-1">Example payload</div>
              <pre className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-[11px] font-mono overflow-x-auto max-h-56 overflow-y-auto" data-testid="jd-webhook-example">
{JSON.stringify(cfg.example_payload, null, 2)}
              </pre>
              <Button variant="ghost" size="sm" className="rounded-full mt-1 text-xs" onClick={() => copy(JSON.stringify(cfg.example_payload, null, 2))}>
                <Copy className="h-3 w-3 mr-1" /> Copy payload
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 rounded-md border border-border p-3 bg-secondary/20">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Total ingested</div>
                <div className="text-xl font-semibold tabular-nums" data-testid="jd-webhook-total">{cfg.stats?.total || 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Kairosa</div>
                <div className="text-xl font-semibold tabular-nums">{cfg.stats?.by_brand?.kairosa || 0}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">JAYDIETs</div>
                <div className="text-xl font-semibold tabular-nums">{cfg.stats?.by_brand?.jaydiets || 0}</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <div className="text-xs text-muted-foreground">
                Tip: idempotent by email — sending the same email twice updates the lead rather than duplicating it.
              </div>
              <Button variant="outline" size="sm" onClick={rotate} disabled={rotating} className="rounded-full gap-1.5" data-testid="jd-webhook-rotate">
                <RotateCcw className="h-3.5 w-3.5" /> {rotating ? "Rotating…" : "Rotate secret"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
